// UI-side helpers for the Executive Challenge surfaces.

// Soft launch: the challenge LOBBY (the entry card + join deep links on the
// home screen) lives on the preview origin only. The same build ships to every
// origin, so this is a runtime hostname gate, not a build flag — the
// production domains hide the entry points; every other origin (the
// executive-challenge branch alias, per-deploy hash URLs, localhost) shows
// them. Existing challenge saves are unaffected either way: their badge,
// result, and report screens are save-driven, not entry-point-driven.
const PRODUCTION_HOSTNAMES = new Set([
	"summerleague.fun",
	"www.summerleague.fun",
	"summerleague.pages.dev",
]);

export const challengeEntryEnabled = (): boolean => {
	try {
		return !PRODUCTION_HOSTNAMES.has(window.location.hostname);
	} catch {
		return false;
	}
};

// The shareable invite: a deep link that lands on the home screen with the
// join sheet open and the code prefilled (the /c/{code} route). Built from the
// current origin, so invites always point at the world the sender plays on.
export const challengeInviteUrl = (code: string): string =>
	`${window.location.origin}/c/${code}`;

export const challengeInviteText = (code: string): string =>
	`Join my Executive Challenge — ${challengeInviteUrl(code)}`;

// Design-token grade palette for challenge surfaces (fine-grained per grade,
// teal → red). The stock report card keeps its own coarser gradeColor(); the
// challenge screens are a distinct brand surface with their own map.
const CHALLENGE_GRADE_COLORS: Record<string, string> = {
	"A+": "#18E0A8",
	A: "#1fd39a",
	"A-": "#2bc98d",
	"B+": "#5bbe86",
	B: "#84b968",
	"B-": "#a8b154",
	"C+": "#d9a441",
	C: "#e8933a",
	"C-": "#ec7f3a",
	D: "#e2613e",
	F: "#E2504E",
};

export const challengeGradeColor = (grade: string): string =>
	CHALLENGE_GRADE_COLORS[grade] ?? "#9fb0bc";

// The verified identity, Phase 1 local stub: the verify sheet runs its full
// phone → OTP → player-ID flow, but "verified" is just a device flag and the
// player ID lives in localStorage. Phase 2 swaps this for the Supabase Auth
// session + user_metadata.player_id.
const VERIFIED_KEY = "slChallengeVerified";
const PLAYER_ID_KEY = "slChallengePlayerId";

export const getStoredVerified = (): boolean => {
	try {
		return localStorage.getItem(VERIFIED_KEY) === "1";
	} catch {
		return false;
	}
};

export const getStoredPlayerId = (): string => {
	try {
		return localStorage.getItem(PLAYER_ID_KEY) ?? "";
	} catch {
		return "";
	}
};

export const storeVerifiedIdentity = (playerId: string) => {
	try {
		localStorage.setItem(VERIFIED_KEY, "1");
		localStorage.setItem(PLAYER_ID_KEY, playerId);
	} catch {
		// Private mode etc. — identity just won't prefill next time.
	}
};
